import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Image from "next/image";
import { CreditCard, Check, Loader2, Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { asset } from "@/lib/asset";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
} from "@/components/ui/modal";

// ============================================================================
// THE MINT Purchase Modal
// ============================================================================

interface PurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  packId: string;
  packName: string;
  price: number;
  priceType: "USD" | "COINS" | "GEMS";
  /**
   * Explicit per-currency prices (NFL packs are coin-native with a distinct
   * USD equivalent). When omitted, prices derive from `price` via the legacy
   * 1 USD = 100 coins rule.
   */
  coinPrice?: number;
  usdPrice?: number;
  userCoins: number;
  userGems: number;
  userUsdCents: number;
  onConfirm: (
    paymentMethod: "USD" | "COINS",
    quantity: number,
  ) => Promise<void>;
}

type Step = "select" | "confirm" | "processing" | "success";

export function PurchaseModal({
  isOpen,
  onClose,
  packId,
  packName,
  price,
  priceType,
  coinPrice,
  usdPrice: usdPriceProp,
  userCoins,
  userGems,
  userUsdCents,
  onConfirm,
}: PurchaseModalProps) {
  const [step, setStep] = useState<Step>("select");
  const [selectedMethod, setSelectedMethod] = useState<"USD" | "COINS">(
    priceType === "GEMS" ? "COINS" : priceType === "USD" ? "USD" : "COINS",
  );
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState<string | null>(null);

  // Calculate prices in different currencies (per pack). Explicit prices win;
  // legacy fallback treats `price` as USD and converts at 100 coins per dollar.
  const usdPrice = usdPriceProp ?? (priceType === "COINS" ? price / 100 : price);
  const coinsPrice = coinPrice ?? (priceType === "COINS" ? price : price * 100);

  // Total prices
  const totalUsd = usdPrice * quantity;
  const totalCoins = coinsPrice * quantity;

  const canAffordCoins = userCoins >= totalCoins;
  const canAffordUsd = userUsdCents >= totalUsd * 100;

  // Max packs user can buy
  const maxCoins = Math.floor(userCoins / coinsPrice) || 0;
  const maxUsd = Math.floor(userUsdCents / (usdPrice * 100)) || 0;

  const handleQuantityChange = (delta: number) => {
    const newQty = Math.max(1, Math.min(10, quantity + delta));
    setQuantity(newQty);
  };

  const handleConfirm = async () => {
    setStep("processing");
    setError(null);

    try {
      await onConfirm(selectedMethod, quantity);
      setStep("success");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Purchase failed");
      setStep("confirm");
    }
  };

  const handleClose = () => {
    setStep("select");
    setQuantity(1);
    setError(null);
    onClose();
  };

  const getMethodPrice = (method: "USD" | "COINS", qty: number = 1) => {
    switch (method) {
      case "USD":
        return `$${(usdPrice * qty).toFixed(2)}`;
      case "COINS":
        return `${(coinsPrice * qty).toLocaleString()} Coins`;
    }
  };

  const getMethodIcon = (method: "USD" | "COINS") => {
    if (method === "COINS") return asset("assets/icons/coins.webp");
    return null; // USD uses CreditCard lucide icon
  };

  const canAfford = (method: "USD" | "COINS") => {
    switch (method) {
      case "USD":
        return canAffordUsd;
      case "COINS":
        return canAffordCoins;
    }
  };

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <ModalContent className="sm:max-w-md">
        <ModalHeader>
          <ModalTitle>
            {step === "success" ? "Purchase Complete!" : `Buy ${packName}`}
          </ModalTitle>
          {step === "select" && (
            <ModalDescription>
              Select quantity and payment method
            </ModalDescription>
          )}
        </ModalHeader>

        <AnimatePresence mode="wait">
          {step === "select" && (
            <motion.div
              key="select"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4 mt-4"
            >
              {/* Quantity Selector */}
              <div className="p-4 rounded-xl bg-surface-3/50 border border-surface-4">
                <div className="flex items-center justify-between">
                  <span className="text-warm-gray">Quantity</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleQuantityChange(-1)}
                      disabled={quantity <= 1}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-colors",
                        quantity <= 1
                          ? "bg-surface-3 text-tertiary cursor-not-allowed"
                          : "bg-surface-4 text-cream hover:bg-tertiary",
                      )}
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-xl font-bold text-cream w-8 text-center font-mono">
                      {quantity}
                    </span>
                    <button
                      onClick={() => handleQuantityChange(1)}
                      disabled={quantity >= 10}
                      className={cn(
                        "h-8 w-8 rounded-lg flex items-center justify-center transition-colors",
                        quantity >= 10
                          ? "bg-surface-3 text-tertiary cursor-not-allowed"
                          : "bg-surface-4 text-cream hover:bg-tertiary",
                      )}
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Payment Methods */}
              <div className="space-y-3">
                {(["COINS", "USD"] as const).map((method) => {
                  const iconSrc = getMethodIcon(method);
                  const affordable = canAfford(method);
                  const isSelected = selectedMethod === method;
                  const maxQty = method === "COINS" ? maxCoins : maxUsd;

                  return (
                    <button
                      key={method}
                      onClick={() => affordable && setSelectedMethod(method)}
                      disabled={!affordable}
                      className={cn(
                        "w-full flex items-center justify-between p-4 rounded-xl border-2 transition-all",
                        isSelected
                          ? "border-gold bg-gold/10"
                          : affordable
                            ? "border-surface-4 hover:border-tertiary bg-surface-3/50"
                            : "border-surface-3 bg-surface-3/30 opacity-50 cursor-not-allowed",
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={cn(
                            "h-10 w-10 rounded-lg flex items-center justify-center",
                            method === "COINS"
                              ? "bg-gold/20"
                              : "bg-verdigris/20",
                          )}
                        >
                          {iconSrc ? (
                            <Image
                              src={iconSrc}
                              alt={method}
                              width={20}
                              height={20}
                              unoptimized
                              className="object-contain"
                            />
                          ) : (
                            <CreditCard className="h-5 w-5 text-verdigris" />
                          )}
                        </div>
                        <div className="text-left">
                          <p className="font-bold text-cream font-mono">
                            {getMethodPrice(method, quantity)}
                          </p>
                          <p className="text-xs text-tertiary">
                            {method === "USD"
                              ? `Balance: $${(userUsdCents / 100).toFixed(2)}`
                              : `Balance: ${userCoins.toLocaleString()}`}
                            {maxQty > 0 && ` (max ${maxQty})`}
                          </p>
                        </div>
                      </div>
                      {isSelected && (
                        <div className="h-6 w-6 rounded-full bg-gold flex items-center justify-center">
                          <Check className="h-4 w-4 text-surface" />
                        </div>
                      )}
                      {!affordable && (
                        <span className="text-xs text-oxblood">
                          Insufficient
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              <Button
                className="w-full"
                size="lg"
                onClick={() => setStep("confirm")}
                disabled={!canAfford(selectedMethod)}
              >
                Continue
              </Button>
            </motion.div>
          )}

          {step === "confirm" && (
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="space-y-4 mt-4"
            >
              <div className="p-4 rounded-xl bg-surface-3/50 border border-surface-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-warm-gray">Pack</span>
                  <span className="font-medium text-cream">{packName}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-warm-gray">Quantity</span>
                  <span className="font-medium text-cream font-mono">
                    {quantity}
                  </span>
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-surface-4">
                  <span className="text-warm-gray">Total</span>
                  <span className="text-lg font-bold text-gold font-mono">
                    {getMethodPrice(selectedMethod, quantity)}
                  </span>
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-oxblood/10 border border-oxblood/30 text-oxblood text-sm">
                  {error}
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep("select")}
                >
                  Back
                </Button>
                <Button className="flex-1" onClick={handleConfirm}>
                  Confirm Purchase
                </Button>
              </div>
            </motion.div>
          )}

          {step === "processing" && (
            <motion.div
              key="processing"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex flex-col items-center justify-center py-8"
            >
              <Loader2 className="h-12 w-12 text-gold animate-spin mb-4" />
              <p className="text-cream font-medium">Processing purchase...</p>
              <p className="text-sm text-warm-gray">
                {quantity > 1 ? `Buying ${quantity} packs` : "Please wait"}
              </p>
            </motion.div>
          )}

          {step === "success" && (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="flex flex-col items-center justify-center py-8"
            >
              <div className="h-16 w-16 rounded-full bg-verdigris/20 flex items-center justify-center mb-4">
                <Image
                  src={asset("assets/icons/sealed_pack.webp")}
                  alt="Pack"
                  width={36}
                  height={36}
                  unoptimized
                  className="object-contain"
                />
              </div>
              <p className="text-cream font-medium mb-2">
                {quantity > 1 ? `${quantity} Packs Added!` : "Pack Added!"}
              </p>
              <p className="text-sm text-warm-gray text-center mb-6">
                Your {quantity > 1 ? "packs are" : `${packName} is`} ready to
                open in your collection
              </p>
              <div className="flex gap-3 w-full">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  className="flex-1"
                  onClick={() => {
                    handleClose();
                    window.location.href = "/collection?tab=packs";
                  }}
                >
                  Open {quantity > 1 ? "Packs" : "Pack"}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </ModalContent>
    </Modal>
  );
}
